import Grid from 'common/components/Grid'
import Skeleton from 'common/components/Skeleton'
import Typography from 'common/components/Typography'
import { NEWS_URL } from 'common/constants'
import theme from 'common/theme'
import GA4 from 'lib/google-analytics-4'
import { useEffect } from 'react'
import useInView from 'react-cool-inview'
import { useTranslation } from 'react-i18next'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const Container = styled.section`
  width: 1180px;
  margin: 40px auto 50px;
`

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ArticleLink = styled.a`
  color: ${theme.colors.orange};
  text-decoration: none;
  cursor: pointer;
`

const Label = styled(Typography)`
  ${theme.typography.rlabel}
  letter-spacing: 2px;
  color: ${theme.colors.orange};
  text-transform: uppercase;
`

const CardsGrid = styled(Grid)`
  padding: 20px 0;
`

const ArticleCardLink = styled.a`
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  background: ${theme.colors.white};
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease;
  &:hover {
    transform: scale(1.02);
  }
`

const PlaceholderCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  background: ${theme.colors.white};
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transform: translateZ(0);
  transition: transform 0.3s ease;
  &:hover {
    transform: scale(1.02);
  }
`

const ArticleImage = styled.div<{ src?: string }>`
  height: 180px;
  background: ${(props) =>
    props.src
      ? `url(${props.src}) center/cover`
      : `${theme.colors.grayLighter}`};
`

const Description = styled.div`
  padding: 24px;
  background: ${theme.colors.white};
  transition: background 0.3s ease, color 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 100px;
  ${ArticleCardLink}:hover & {
    background: #6e021a;
    color: ${theme.colors.white};
  }
  ${PlaceholderCard}:hover & {
    background: #6e021a;
    color: ${theme.colors.white};
  }
`

const ArticleTitle = styled(Typography)`
  font-weight: 700;
  margin: 0;
  color: inherit;
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const MetaLabel = styled(Typography)`
  ${theme.typography.rparagraph}
  color: inherit;
  margin: 0;
  opacity: 0.8;
`

function Article({ title }: { title?: string }): JSX.Element {
  const { t, i18n } = useTranslation('home')
  const { isLoading, data } = useStoreState((state) => state.article)
  const { getPressRelease } = useStoreActions(
    (actions) => actions.article,
  )
  const { observe, inView } = useInView<HTMLDivElement>({
    unobserveOnEnter: true,
  })

  const getImage = (text: string): string => {
    const matches = text.match(
      /(https?:\/\/.*\.(?:png|jpg|jpeg|webp|avif))/i,
    )
    return matches[0]
  }

  useEffect(() => {
    if (inView) {
      getPressRelease({
        categories: 201,
      })
    }
  }, [getPressRelease, inView, i18n.language])

  const { trackEvent } = GA4()

  return (
    <Container data-testid="article-test" ref={observe}>
      <HeaderContainer>
        <div>
          <Label type="rlabel">{t('ARTICLE_EXPOSURE_LABEL')}</Label>
          <Typography type="bh2" marginTop={8}>
            {title || t('ARTICLE_TITLE')}
          </Typography>
        </div>
        <ArticleLink href={NEWS_URL} target="_blank" rel="noreferrer">
          {t('SEE_ALL')}
        </ArticleLink>
      </HeaderContainer>
      <CardsGrid
        templateColumn="auto-fit"
        templateColumnSize="minmax(320px, 1fr)"
        columnGap="32px"
      >
        {isLoading &&
          new Array(3)
            .fill(null)
            .map((_, index) => (
              <Skeleton
                key={`skeleton-article-${index.toString()}`}
                width="100%"
                height="280px"
              />
            ))}
        {!isLoading &&
          data.slice(0, 3).map((item, index) => {
            const image = getImage(item.yoast_head)
            const handleClickArticle = () => {
              trackEvent({
                params: {
                  panel_name: 'Artikel Seputar Bisnis',
                  panel_page_title: item.title.rendered,
                  panel_page_url: item.link,
                  panel_position: `Slot - ${index + 1}`,
                },
                eventCategory: 'Panel Tracking',
                eventName: 'panel_tracking',
              })
            }

            return (
              <ArticleCardLink
                href={item.link}
                target="_blank"
                rel="noreferrer"
                key={String(`article-${index}`)}
                onClick={handleClickArticle}
              >
                <ArticleImage src={image} />
                <Description>
                  <ArticleTitle type="rh5" color="inherit">
                    {item.title.rendered}
                  </ArticleTitle>
                  <MetaRow>
                    <MetaLabel type="rparagraph">
                      {new Date(item.date).toLocaleDateString(
                        i18n.language === 'en' ? 'en-US' : 'id-ID',
                        {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        },
                      )}
                    </MetaLabel>
                  </MetaRow>
                </Description>
              </ArticleCardLink>
            )
          })}
      </CardsGrid>
    </Container>
  )
}

export default Article
